"use client";

const LOCAL_FIRST_DEFAULT_GC_TIME = 1000 * 60 * 60 * 24;
const LOCAL_FIRST_STORAGE_PREFIX = "yn-local-first";
const LOCAL_FIRST_DEFAULT_MAX_AGE = 1000 * 60 * 60 * 12;
/** 同一「主题前缀」下最多保留的条目数，超出时淘汰最旧的 */
const LOCAL_FIRST_MAX_ENTRIES_PER_PREFIX = 20;

export type LocalFirstScope = "public" | "user";
type TopicEventsCacheMode = "sport" | "normal" | "worldCupGames";

export type LocalFirstCachePayload<T> = { data: T; updatedAt: number };

function getLocalFirstStorage() {
    if ( typeof window === "undefined" ) {
        return undefined;
    }

    return window.localStorage;
}

function getScopeStoragePrefix( scope: LocalFirstScope ) {
    return `${LOCAL_FIRST_STORAGE_PREFIX}:${scope}:`;
}

export function createLocalFirstQueryOptions() {
    return { gcTime: LOCAL_FIRST_DEFAULT_GC_TIME, staleTime: 0 } as const;
}

function hashUserToken( token: string ) {
    let hash = 2166136261;

    for ( let i = 0; i < token.length; i += 1 ) {
        hash ^= token.charCodeAt( i );
        hash = Math.imul( hash, 16777619 );
    }

    return ( hash >>> 0 ).toString( 36 );
}

export function getLocalFirstUserDimensionKey( userId?: number | null, token?: string ) {
    if ( userId && userId > 0 ) {
        return `uid:${userId}`;
    }

    if ( token ) {
        return `token:${hashUserToken( token )}`;
    }

    return "guest";
}

export function buildLocalFirstStorageKey(
    scope: LocalFirstScope,
    ...segments: Array<string | number | null | undefined>
) {
    const normalizedSegments = segments.filter( ( segment ) =>
        segment !== null && segment !== undefined && segment !== ""
    ).map( ( segment ) => encodeURIComponent( String( segment ) ) );

    return `${getScopeStoragePrefix( scope )}${normalizedSegments.join( ":" )}`;
}

export const localFirstCacheKeys = {
    public: {
        topicTree: () => buildLocalFirstStorageKey( "public", "topicTree" ),
        homeBanner: () => buildLocalFirstStorageKey( "public", "homeBanner" ),
        quickEntryList: () => buildLocalFirstStorageKey( "public", "quickEntryList" ),
        cryptoPrice5sRecent: ( params: { symbolId?: string | number; thirdType?: string | number } ) =>
            buildLocalFirstStorageKey( "public", "cryptoPrice5sRecent", params.symbolId, params.thirdType ),
    },
    user: {
        cricketLiveEvents: ( userDimensionKey: string ) =>
            buildLocalFirstStorageKey( "user", "cricketLiveEvents", userDimensionKey ),
        eventDetail: ( userDimensionKey: string, eventNo?: string | null ) =>
            buildLocalFirstStorageKey( "user", "eventDetail", userDimensionKey, eventNo ),
        sportsDetail: ( userDimensionKey: string, eventNo?: string | null ) =>
            buildLocalFirstStorageKey( "user", "sportsDetail", userDimensionKey, eventNo ),
        cryptoDetail: ( userDimensionKey: string, eventNo?: string | null ) =>
            buildLocalFirstStorageKey( "user", "cryptoDetail", userDimensionKey, eventNo ),
        topicEvents: (
            userDimensionKey: string,
            params: { topicId?: string | number | null; mode: TopicEventsCacheMode; timeRange?: string | null },
        ) => buildLocalFirstStorageKey(
            "user",
            "topicEvents",
            params.mode,
            userDimensionKey,
            params.topicId,
            params.mode !== "normal" ? params.timeRange : null,
        ),
        guessDetail: ( userDimensionKey: string, guessNo?: string | null ) =>
            buildLocalFirstStorageKey( "user", "guessDetail", userDimensionKey, guessNo ),
    },
} as const;

export const localFirstQueryKeys = {
    homeBanner: () => [ "homeBanner" ] as const,
    quickEntryList: () => [ "quickEntryList" ] as const,
    cryptoPrice5sRecent: ( params: { symbolId?: string | number; thirdType?: string | number } ) =>
        [ "cryptoPrice5sRecent", params.symbolId, params.thirdType ] as const,
    cricketLiveEvents: ( userDimensionKey: string ) => [ "cricketLiveEvents", userDimensionKey ] as const,
    eventDetail: ( eventNo: string | null | undefined, userDimensionKey: string ) =>
        [ "eventDetail", "byEvent", eventNo, userDimensionKey ] as const,
    sportsDetail: ( eventNo: string | null | undefined, userDimensionKey: string ) =>
        [ "sportsDetail", eventNo, userDimensionKey ] as const,
    cryptoDetail: ( eventNo: string | null | undefined, userDimensionKey: string ) =>
        [ "cryptoDetail", eventNo, userDimensionKey ] as const,
    topicEvents: (
        params: {
            topicId?: string | number | null;
            mode: TopicEventsCacheMode;
            sportTimeRange?: string | null;
            userDimensionKey: string;
        },
    ) => [
        "topicEvents",
        params.topicId,
        params.mode,
        params.mode !== "normal" ? ( params.sportTimeRange ?? null ) : null,
        params.userDimensionKey,
    ] as const,
    guessDetail: ( guessNo: string | null | undefined, userDimensionKey: string ) =>
        [ "guessDetail", guessNo, userDimensionKey ] as const,
} as const;

export function getLocalFirstInitialData<T>( cached?: LocalFirstCachePayload<T> ) {
    return { initialData: cached?.data, initialDataUpdatedAt: cached?.updatedAt } as const;
}

export function readLocalFirstQueryCache<T>( storageKey: string, maxAge = LOCAL_FIRST_DEFAULT_MAX_AGE ) {
    const storage = getLocalFirstStorage();

    if ( !storage ) {
        return undefined;
    }

    try {
        const raw = storage.getItem( storageKey );

        if ( !raw ) {
            return undefined;
        }

        const payload = JSON.parse( raw ) as LocalFirstCachePayload<T>;

        if ( !payload?.updatedAt || Date.now() - payload.updatedAt > maxAge ) {
            storage.removeItem( storageKey );
            return undefined;
        }

        return payload;
    } catch {
        storage.removeItem( storageKey );
        return undefined;
    }
}

export function writeLocalFirstQueryCache<T>( storageKey: string, data: T ) {
    const storage = getLocalFirstStorage();

    if ( !storage ) {
        return;
    }

    // 写入前：对同前缀条目做被动 GC + LRU 淘汰，防止 localStorage 无限膨胀
    evictByPrefix( storage, storageKey );

    const payload: LocalFirstCachePayload<T> = { data, updatedAt: Date.now() };

    try {
        storage.setItem( storageKey, JSON.stringify( payload ) );
    } catch {
        // QuotaExceededError：存储已满，强制清理当前 scope 后重试
        const scope: LocalFirstScope = storageKey.includes( ":user:" ) ? "user" : "public";
        clearLocalFirstQueryStorage( scope );
        try {
            storage.setItem( storageKey, JSON.stringify( payload ) );
        } catch {
            // 仍然失败则放弃，不影响功能
        }
    }
}

/**
 * 从 storageKey 中提取「主题前缀」
 * 规则：取前三段（prefix:scope:topic），用于归组同类缓存
 * 例如：yn-local-first:user:eventDetail:uid:123:evtabc → yn-local-first:user:eventDetail
 */
function getTopicPrefix( storageKey: string ): string {
    const parts = storageKey.split( ":" );
    // 取前3段：[yn-local-first, scope, topic]
    return parts.slice( 0, 3 ).join( ":" );
}

/**
 * 对同前缀的条目执行：
 * 1. 清理过期条目（被动 GC）
 * 2. 若仍超过上限，按 updatedAt 升序删除最旧的（LRU Eviction）
 */
function evictByPrefix( storage: Storage, currentKey: string ) {
    const prefix = getTopicPrefix( currentKey );
    const now = Date.now();
    const entries: Array<{ key: string; updatedAt: number }> = [];

    for ( let i = 0; i < storage.length; i++ ) {
        const key = storage.key( i );
        if ( !key || !key.startsWith( prefix ) || key === currentKey ) continue;

        try {
            const raw = storage.getItem( key );
            if ( !raw ) continue;
            const payload = JSON.parse( raw ) as LocalFirstCachePayload<unknown>;

            // 1. 直接清理过期的
            if ( !payload?.updatedAt || now - payload.updatedAt > LOCAL_FIRST_DEFAULT_MAX_AGE ) {
                storage.removeItem( key );
                i--; // 删除后 index 后移
                continue;
            }

            entries.push( { key, updatedAt: payload.updatedAt } );
        } catch {
            storage.removeItem( key );
            i--;
        }
    }

    // 2. 超过上限时，删除最旧的（为即将写入的新条目腾位置，所以上限 -1）
    if ( entries.length >= LOCAL_FIRST_MAX_ENTRIES_PER_PREFIX ) {
        entries.sort( ( a, b ) => a.updatedAt - b.updatedAt ).slice(
            0,
            entries.length - LOCAL_FIRST_MAX_ENTRIES_PER_PREFIX + 1,
        ).forEach( ( entry ) => storage.removeItem( entry.key ) );
    }
}

export function clearLocalFirstQueryStorage( scope: LocalFirstScope ) {
    const storage = getLocalFirstStorage();

    if ( !storage ) {
        return;
    }

    const keyPrefix = getScopeStoragePrefix( scope );
    const keysToRemove: string[] = [];

    for ( let i = 0; i < storage.length; i += 1 ) {
        const key = storage.key( i );

        if ( key?.startsWith( keyPrefix ) ) {
            keysToRemove.push( key );
        }
    }

    keysToRemove.forEach( ( key ) => storage.removeItem( key ) );
}

export function clearUserScopedLocalFirstQueryStorage() {
    clearLocalFirstQueryStorage( "user" );
}
